import { Component, EventEmitter, Input, Output } from "@angular/core";
import * as moment from "moment";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";
import { getReadableTimeDifference } from "../../ticket/view/components/ticket-timeline/ticket-timeline.component";

@Component({
  selector: "app-support-dashboard-ticket-detail",
  templateUrl: "./support-dashboard-ticket-detail.component.html",
  styleUrls: [
    "../../ticket/ticket.component.css",
    "./support-dashboard-ticket-detail.component.css",
  ],
})
export class SupportDashboardTicketDetailComponent {
  @Input() ticket: any = null;
  @Output() back = new EventEmitter<void>();

  modalTitle = "Reply";
  replyTicket_: any = null;
  addKB: any = null;
  correspondence: any[] = [];
  correspondenceHeader: Array<{ id: string; name: string }> = [
    { id: "submittedBy", name: "Sender" },
    { id: "timeCreated", name: "Time" },
    { id: "body", name: "Message" },
  ];
  status_history: any[] = [];
  participants = "";
  viewer: any = null;
  readonly urlPrefix =
    localStorage.getItem("role") == "Admin" ? "admin" : "client";

  constructor(
    private apiService: APIService,
    private notifier: NotifierService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadCorrespondence();
  }

  async loadCorrespondence(): Promise<void> {
    if (!this.ticket?.issue_key) {
      return;
    }

    this.notifier.loading(true);
    this.status_history = [];

    try {
      const result = await this.apiService.postDataPromis(
        `${APIService.API_ENDPOINTV3}/${this.urlPrefix}/support`,
        {
          action: "jira_comm",
          issue_key: this.ticket.issue_key,
        },
        this.getRequestHeaders()
      );

      if (result.status == "1" || result.s == "1") {
        this.correspondence = result.issues_details.comments || [];

        if (result?.issues_details?.first_response_sla) {
          result.issues_details.first_response_sla["time_completed_str"] =
            getReadableTimeDifference(
              moment(),
              moment().add(
                result.issues_details.first_response_sla["time_completed"],
                "milliseconds"
              )
            );
          result.issues_details.first_response_sla["time_remaining_str"] =
            getReadableTimeDifference(
              moment(),
              moment().add(
                result.issues_details.first_response_sla["time_remaining"],
                "milliseconds"
              )
            );
          result.issues_details.first_response_sla["total_time_str"] =
            getReadableTimeDifference(
              moment(),
              moment().add(
                result.issues_details.first_response_sla["total_time"],
                "milliseconds"
              )
            );
        }

        if (result?.issues_details?.resolution_sla) {
          result.issues_details.resolution_sla["time_completed_str"] =
            getReadableTimeDifference(
              moment(),
              moment().add(
                result.issues_details.resolution_sla["time_completed"],
                "milliseconds"
              )
            );
          result.issues_details.resolution_sla["time_remaining_str"] =
            getReadableTimeDifference(
              moment(),
              moment().add(
                result.issues_details.resolution_sla["time_remaining"],
                "milliseconds"
              )
            );
          result.issues_details.resolution_sla["total_time_str"] =
            getReadableTimeDifference(
              moment(),
              moment().add(
                result.issues_details.resolution_sla["total_time"],
                "milliseconds"
              )
            );

          if (result.issues_details.resolution_sla.breached) {
            result.issues_details.resolution_sla["breached_by_str"] =
              getReadableTimeDifference(
                moment(),
                moment().add(
                  result.issues_details.resolution_sla["time_completed"] -
                    result.issues_details.resolution_sla["total_time"],
                  "milliseconds"
                )
              );
          }
        }

        this.ticket.first_response_sla =
          result.issues_details.first_response_sla || undefined;
        this.ticket.resolution_sla =
          result.issues_details.resolution_sla || undefined;
        this.participants = Array.isArray(result?.issues_details?.participants)
          ? result.issues_details.participants.join(", ")
          : "";
        this.status_history = result.issues_details.status_history || [];
        this.status_history.unshift({
          name: this.ticket.creator,
          email: "",
          time: this.ticket.created,
          status: "Created",
        });

        this.correspondence = this.correspondence.map((chat: any) => {
          if (chat.body) {
            const chatLines = String(chat.body).split("\n");
            if (chatLines.length > 0) {
              chat.body = chatLines
                .map((line: string) => {
                  const chatElement = document.createElement("p");
                  chatElement.classList.add("chat_td");
                  chatElement.style.margin = "0";
                  chatElement.innerHTML = line;
                  return chatElement.outerHTML;
                })
                .join(" ");
            }
          }

          if (chat.attachments && chat.attachments.length > 0) {
            chat.attachments = chat.attachments.map((attachment: any) => {
              if (attachment.file_type.indexOf("image") > -1) {
                attachment["src"] = "LOADING";
              }
              this.loadCorrespondenceImage(attachment);
              return attachment;
            });
          }

          if (chat.body) {
            chat.body = chat.body.replaceAll("\n", "");
          }

          return chat;
        });
      } else {
        this.notifier.alert(
          "Error",
          "",
          result.error_message || "Unable to load ticket details.",
          "error",
          5000
        );
      }
    } finally {
      this.notifier.loading(false);
    }
  }

  async loadCorrespondenceImage(attachment: any): Promise<void> {
    if (!attachment?.file_type || attachment.file_type.indexOf("image") < 0) {
      return;
    }

    attachment["src"] = "LOADING";

    const result = await this.apiService.postDataPromis(
      `${APIService.API_ENDPOINTV3}/${this.urlPrefix}/support`,
      {
        url: attachment["file_url"],
        action: "download_jira_attachments",
      },
      this.getRequestHeaders()
    );

    if (result.status == "1" || result.status == 1) {
      attachment.src = `data:${attachment.file_type};base64,${result.data}`;
    }
  }

  viewImage(attachment: any): void {
    this.viewer = { ...attachment, show: false, width: 50 };
    setTimeout(() => {
      if (this.viewer) {
        this.viewer.show = true;
      }
    }, 1);
  }

  hideImage(): void {
    if (!this.viewer) {
      return;
    }

    this.viewer.show = false;
    setTimeout(() => {
      this.viewer = null;
    }, 200);
  }

  async downloadAttachment(attachment: any): Promise<void> {
    const result = await this.apiService.postDataPromis(
      `${APIService.API_ENDPOINTV3}/${this.urlPrefix}/support`,
      {
        url: attachment["file_url"],
        action: "download_jira_attachments",
      },
      this.getRequestHeaders()
    );

    if (result.status == "1" || result.status == 1) {
      const url = `data:${attachment.file_type};base64,${result.data}`;
      const el = document.createElement("a");
      el.setAttribute("href", url);
      el.setAttribute("download", attachment.file_name);
      el.click();
    }
  }

  goBack(): void {
    this.back.emit();
  }

  replyTicket(): void {
    this.modalTitle = "Reply";
    this.replyTicket_ = this.ticket;
  }

  addToKB(): void {
    this.addKB = this.ticket;
    this.addKB["history"] = this.correspondence
      .map((chat: any) => {
        return chat.body;
      })
      .join("\n");

    this.addKB.new = true;
    this.addKB["subject"] = this.addKB["summary"];
    delete this.addKB["summary"];
  }

  hideAddKB(): void {
    this.addKB = null;
  }

  async hideModal(event: any): Promise<void> {
    this.replyTicket_ = null;
    if (event) {
      await this.loadCorrespondence();
    }
  }

  private getRequestHeaders(): { Authorization: string | null } {
    return {
      Authorization: localStorage.getItem("t"),
    };
  }
}
